import { useState } from "react";
import { Link } from "react-router-dom";
import ColorSwatches from "./ColorSwatches";
import StarRating from "./StarRating";
import NewBadge from "./NewBadge";
import { IconPlus } from "./icons";
import { useCurrency } from "../context/CurrencyContext";
import { useAuth } from "../context/AuthContext";
import { useSiteMode } from "../context/SiteModeContext";
import { useCart } from "../context/CartContext";
import { effectivePrice } from "../lib/pricing";
import { variantSku } from "../lib/variant";
import styles from "./ItemCard.module.css";

// Card on listing pages. Hovering a swatch previews that color's image
// without changing the underlying selection. The quick-add button uses
// the active (hovered or first) color and the first size, which matches
// the public storefront's behaviour and routes the user to PDP if the
// item has multiple sizes that need explicit choice.
export default function ItemCard({ item }) {
  const { formatPrice } = useCurrency();
  const { user } = useAuth();
  const { effectiveMode } = useSiteMode();
  const { add } = useCart();
  const [hovered, setHovered] = useState(null);
  if (!item) return null;
  const href = `/product/${encodeURIComponent(item.id ?? item.sku)}`;
  const activeColor = hovered || item.colors?.[0] || null;
  const displayed = activeColor?.image || item.image;
  const priced = effectivePrice(item, user, { color: activeColor, siteMode: effectiveMode });

  function quickAdd(e) {
    e.preventDefault();
    e.stopPropagation();
    const size = item.sizes?.length ? item.sizes[0] : null;
    const sku = variantSku(item, activeColor, size);
    add(sku, 1, {
      name: item.name,
      image: activeColor?.image || item.image,
      price: priced?.price?.amount,
      color: activeColor ? { name: activeColor.name, hex: activeColor.hex, image: activeColor.image } : null,
      size,
    });
  }

  return (
    <article className={styles.card}>
      {item.isNew && <NewBadge />}
      <Link to={href} className={styles.imageLink} aria-label={item.name}>
        {displayed ? (
          <img src={displayed} alt={item.name} className={styles.image} loading="lazy" />
        ) : (
          <div className={styles.imagePlaceholder} />
        )}
        <button
          type="button"
          className={styles.quickAdd}
          onClick={quickAdd}
          aria-label={`Add ${item.name} to cart`}
          title="Quick add"
        >
          <IconPlus size={18} />
          <span className={styles.quickAddText}>Add</span>
        </button>
      </Link>
      <div className={styles.body}>
        {item.colors?.length ? (
          <div className={styles.swatches}>
            <ColorSwatches colors={item.colors} size="sm" onHover={setHovered} />
          </div>
        ) : null}
        <Link to={href} className={styles.name}>{item.name}</Link>
        <div className={styles.price}>{formatPrice(priced)}</div>
        {(item.rating != null || item.reviewCount != null) && (
          <StarRating value={item.rating || 0} count={item.reviewCount} size="sm" />
        )}
      </div>
    </article>
  );
}
